import { useRef, useEffect, useCallback } from 'react';

export default function ParticleField({ className = '', heroMode = false }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const pointerRef = useRef({ x: -9999, y: -9999, active: false });
  const visibleRef = useRef(true);

  const init = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio, 2);

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const w = () => canvas.width / dpr;
    const h = () => canvas.height / dpr;

    const getCircle = () => {
      const cw = w();
      const ch = h();
      const size = Math.min(cw, ch) * 0.62;
      return { cx: cw / 2, cy: ch / 2, r: size / 2 };
    };

    const totalCount = heroMode ? 120 : 60;
    const particles = [];
    const circle = getCircle();

    for (let i = 0; i < totalCount; i++) {
      let x, y;

      if (heroMode) {
        // Place particles in the RING zone (between 55% and 110% of radius)
        // This keeps the center/text area clean
        const minDist = circle.r * 0.55;
        const maxDist = circle.r * 1.4;
        let tries = 0;
        do {
          x = Math.random() * w();
          y = Math.random() * h();
          tries++;
          const dist = Math.hypot(x - circle.cx, y - circle.cy);
          if (dist >= minDist && dist <= maxDist) break;
          if (dist > maxDist) break; // outside particles are fine
        } while (tries < 80);
      } else {
        x = Math.random() * w();
        y = Math.random() * h();
      }

      const isBright = Math.random() < 0.1;
      particles.push({
        x, y, homeX: x, homeY: y,
        vx: 0, vy: 0,
        size: isBright ? 2 + Math.random() * 3 : 0.5 + Math.random() * 2,
        baseOpacity: isBright ? 0.5 + Math.random() * 0.4 : 0.08 + Math.random() * 0.25,
        opacity: 0,
        drift: Math.random() * Math.PI * 2,
        driftSpeed: 0.001 + Math.random() * 0.003,
        isBright,
        twinkleTimer: Math.random() * 4000,
        twinkleInterval: 2000 + Math.random() * 4000,
        twinkleDuration: 400 + Math.random() * 600,
        isTwinkling: false,
        twinkleStart: 0,
      });
    }

    const sparks = [];
    let nextSparkTime = heroMode ? performance.now() + 1500 + Math.random() * 3000 : Infinity;
    const shootingStars = [];
    let nextShootTime = heroMode ? performance.now() + 3000 + Math.random() * 6000 : Infinity;

    const ATTRACT_RADIUS = 280;
    const ATTRACT_FORCE = 0.005;
    const HOME_FORCE = 0.004;

    let lastTime = performance.now();

    const animate = (now) => {
      if (!visibleRef.current) { animRef.current = requestAnimationFrame(animate); return; }
      const dt = Math.min(now - lastTime, 32);
      lastTime = now;
      const cw = w();
      const ch = h();
      ctx.clearRect(0, 0, cw, ch);

      const px = pointerRef.current.x;
      const py = pointerRef.current.y;
      const pActive = pointerRef.current.active;
      const circ = getCircle();

      for (const p of particles) {
        p.drift += p.driftSpeed;
        p.vx += Math.sin(p.drift) * 0.006;
        p.vy += Math.cos(p.drift * 0.7) * 0.006;

        if (pActive) {
          const dx = px - p.x;
          const dy = py - p.y;
          const dist = Math.hypot(dx, dy);
          if (dist < ATTRACT_RADIUS && dist > 5) {
            const force = (1 - dist / ATTRACT_RADIUS) * ATTRACT_FORCE;
            p.vx += (dx / dist) * force;
            p.vy += (dy / dist) * force;
          }
        }

        p.opacity += (p.baseOpacity - p.opacity) * 0.03;

        const hx = p.homeX - p.x;
        const hy = p.homeY - p.y;
        p.vx += hx * HOME_FORCE;
        p.vy += hy * HOME_FORCE;
        p.vx *= 0.94;
        p.vy *= 0.94;
        p.x += p.vx;
        p.y += p.vy;

        if (p.x < -20) { p.x = cw + 20; p.homeX = cw * Math.random(); }
        if (p.x > cw + 20) { p.x = -20; p.homeX = cw * Math.random(); }
        if (p.y < -20) { p.y = ch + 20; p.homeY = ch * Math.random(); }
        if (p.y > ch + 20) { p.y = -20; p.homeY = ch * Math.random(); }

        p.twinkleTimer -= dt;
        if (p.twinkleTimer <= 0 && !p.isTwinkling) {
          p.isTwinkling = true;
          p.twinkleStart = now;
          p.twinkleTimer = p.twinkleInterval;
        }

        let renderOpacity = Math.min(p.opacity, 0.9);
        if (p.isTwinkling) {
          const elapsed = now - p.twinkleStart;
          if (elapsed > p.twinkleDuration) p.isTwinkling = false;
          else renderOpacity = Math.min(p.baseOpacity + Math.sin((elapsed / p.twinkleDuration) * Math.PI) * 0.6, 1);
        }

        const distToCenter = Math.hypot(p.x - circ.cx, p.y - circ.cy);
        const inside = heroMode && distToCenter < circ.r * 0.9;

        let r, g, b;
        if (inside) {
          const centerFactor = 1 - distToCenter / (circ.r * 0.9);
          r = Math.round(200 + centerFactor * 55);
          g = Math.round(180 + centerFactor * 60);
          b = Math.round(120 + centerFactor * 80);
        } else {
          r = 180; g = 148; b = 60;
        }

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${renderOpacity})`;
        ctx.fill();

        if (p.isBright) {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size * 3, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${renderOpacity * 0.1})`;
          ctx.fill();
        }
      }

      // Sparks
      if (heroMode) {
        if (now >= nextSparkTime) {
          const angle = Math.random() * Math.PI * 2;
          const dist = circ.r * 0.5 + Math.random() * circ.r * 0.4;
          const scx = circ.cx + Math.cos(angle) * dist;
          const scy = circ.cy + Math.sin(angle) * dist;
          const count = 3 + Math.floor(Math.random() * 4);
          for (let s = 0; s < count; s++) {
            const a = Math.random() * Math.PI * 2;
            const spd = 0.3 + Math.random() * 1.5;
            sparks.push({
              x: scx + (Math.random() - 0.5) * 5,
              y: scy + (Math.random() - 0.5) * 5,
              vx: Math.cos(a) * spd,
              vy: Math.sin(a) * spd - 0.2,
              life: 1, decay: 0.012 + Math.random() * 0.016,
              size: 0.6 + Math.random() * 1.8,
              hue: Math.random() > 0.3 ? 0 : 1,
            });
          }
          nextSparkTime = now + 2500 + Math.random() * 5000;
        }

        for (let i = sparks.length - 1; i >= 0; i--) {
          const s = sparks[i];
          s.x += s.vx; s.y += s.vy;
          s.vy += 0.012; s.vx *= 0.985;
          s.life -= s.decay;
          if (s.life <= 0) { sparks.splice(i, 1); continue; }
          const al = s.life * 0.85;
          const sr = s.hue === 0 ? 232 : 255;
          const sg = s.hue === 0 ? 168 : 110;
          const sb = s.hue === 0 ? 50 : 25;
          ctx.beginPath();
          ctx.arc(s.x, s.y, s.size * 3, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${sr},${sg},${sb},${al * 0.12})`;
          ctx.fill();
          ctx.beginPath();
          ctx.arc(s.x, s.y, s.size, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${sr},${sg},${sb},${al})`;
          ctx.fill();
          ctx.beginPath();
          ctx.arc(s.x, s.y, s.size * 0.3, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255,240,200,${al * 0.8})`;
          ctx.fill();
        }

        // Shooting stars
        if (now >= nextShootTime) {
          const side = Math.random();
          let sx, sy, sa;
          if (side < 0.5) {
            sx = circ.cx - circ.r * 0.6 + Math.random() * circ.r * 1.2;
            sy = circ.cy - circ.r * 0.8;
            sa = Math.PI * 0.25 + Math.random() * Math.PI * 0.5;
          } else {
            sx = side < 0.75 ? circ.cx - circ.r * 0.8 : circ.cx + circ.r * 0.8;
            sy = circ.cy - circ.r * 0.3 + Math.random() * circ.r * 0.4;
            sa = side < 0.75 ? (0.2 + Math.random() * 0.5) : (Math.PI - 0.2 - Math.random() * 0.5);
          }
          const spd = 3 + Math.random() * 4;
          shootingStars.push({
            x: sx, y: sy,
            vx: Math.cos(sa) * spd, vy: Math.sin(sa) * spd,
            life: 1, decay: 0.013 + Math.random() * 0.008,
            length: 30 + Math.random() * 50, width: 0.6 + Math.random() * 1,
          });
          nextShootTime = now + 5000 + Math.random() * 10000;
        }

        for (let i = shootingStars.length - 1; i >= 0; i--) {
          const ss = shootingStars[i];
          ss.x += ss.vx; ss.y += ss.vy; ss.life -= ss.decay;
          if (ss.life <= 0 || ss.x < -80 || ss.x > cw + 80 || ss.y > ch + 80) {
            shootingStars.splice(i, 1); continue;
          }
          const al = ss.life;
          const mag = Math.hypot(ss.vx, ss.vy);
          const nx = ss.vx / mag;
          const ny = ss.vy / mag;
          const tx = ss.x - nx * ss.length * al;
          const ty = ss.y - ny * ss.length * al;
          const grad = ctx.createLinearGradient(tx, ty, ss.x, ss.y);
          grad.addColorStop(0, 'rgba(255,255,255,0)');
          grad.addColorStop(0.6, `rgba(232,197,71,${al * 0.3})`);
          grad.addColorStop(1, `rgba(255,255,255,${al * 0.85})`);
          ctx.beginPath(); ctx.moveTo(tx, ty); ctx.lineTo(ss.x, ss.y);
          ctx.strokeStyle = grad; ctx.lineWidth = ss.width; ctx.lineCap = 'round'; ctx.stroke();
          ctx.beginPath(); ctx.arc(ss.x, ss.y, ss.width + 0.5, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255,255,255,${al * 0.9})`; ctx.fill();
          ctx.beginPath(); ctx.arc(ss.x, ss.y, ss.width * 4, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(232,197,71,${al * 0.1})`; ctx.fill();
        }
      }

      // Subtle cursor glow
      if (pActive) {
        const glow = ctx.createRadialGradient(px, py, 0, px, py, 150);
        glow.addColorStop(0, 'rgba(201,168,76,0.025)');
        glow.addColorStop(1, 'rgba(201,168,76,0)');
        ctx.fillStyle = glow;
        ctx.fillRect(0, 0, cw, ch);
      }

      animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', resize);
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [heroMode]);

  useEffect(() => {
    const cleanup = init();
    const canvas = canvasRef.current;

    const getPos = (cx, cy) => {
      const r = canvas?.getBoundingClientRect();
      return r ? { x: cx - r.left, y: cy - r.top } : { x: -9999, y: -9999 };
    };

    const onMouse = (e) => { pointerRef.current = { ...getPos(e.clientX, e.clientY), active: true }; };
    const onLeave = () => { pointerRef.current = { x: -9999, y: -9999, active: false }; };
    const onTouch = (e) => {
      if (e.touches.length > 0) {
        const t = e.touches[0];
        pointerRef.current = { ...getPos(t.clientX, t.clientY), active: true };
      }
    };
    const onTouchEnd = () => { pointerRef.current = { x: -9999, y: -9999, active: false }; };

    window.addEventListener('mousemove', onMouse);
    window.addEventListener('mouseleave', onLeave);
    window.addEventListener('touchstart', onTouch, { passive: true });
    window.addEventListener('touchmove', onTouch, { passive: true });
    window.addEventListener('touchend', onTouchEnd);

    const obs = new IntersectionObserver(([e]) => { visibleRef.current = e.isIntersecting; }, { threshold: 0 });
    if (canvas) obs.observe(canvas);

    return () => {
      cleanup?.();
      window.removeEventListener('mousemove', onMouse);
      window.removeEventListener('mouseleave', onLeave);
      window.removeEventListener('touchstart', onTouch);
      window.removeEventListener('touchmove', onTouch);
      window.removeEventListener('touchend', onTouchEnd);
      obs.disconnect();
    };
  }, [init]);

  return <canvas ref={canvasRef} className={`absolute inset-0 w-full h-full pointer-events-none ${className}`} />;
}
